import * as React from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '../../lib/utils';

function Sheet({ ...props }) {
    return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }) {
    return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }) {
    return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ children, ...props }) {
    return (
        <SheetPrimitive.Portal data-slot="sheet-portal" {...props}>
            <div className="tailwind-scope tw-font-theme">{children}</div>
        </SheetPrimitive.Portal>
    );
}

function SheetOverlay({ className, ...props }) {
    return (
        <SheetPrimitive.Overlay
            data-slot="sheet-overlay"
            className={cn(
                'tw-fixed tw-inset-0 tw-z-50 tw-bg-black/50 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0',
                className
            )}
            {...props}
        />
    );
}

function SheetContent({ className, children, side = 'right', overlayClassName, ...props }) {
    return (
        <SheetPortal>
            <SheetOverlay className={overlayClassName} />
            <SheetPrimitive.Content
                data-slot="sheet-content"
                className={cn(
                    'tw-fixed tw-z-50 tw-flex tw-flex-col tw-gap-4 tw-bg-background tw-shadow-lg tw-transition tw-ease-in-out data-[state=closed]:tw-duration-300 data-[state=open]:tw-duration-500 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out',
                    side === 'right' &&
                        'tw-inset-y-0 tw-right-0 tw-h-full tw-w-3/4 tw-border-l data-[state=closed]:tw-slide-out-to-right data-[state=open]:tw-slide-in-from-right sm:tw-max-w-sm',
                    side === 'left' &&
                        'tw-inset-y-0 tw-left-0 tw-h-full tw-w-3/4 tw-border-r data-[state=closed]:tw-slide-out-to-left data-[state=open]:tw-slide-in-from-left sm:tw-max-w-sm',
                    side === 'top' &&
                        'tw-inset-x-0 tw-top-0 tw-h-auto tw-border-b data-[state=closed]:tw-slide-out-to-top data-[state=open]:tw-slide-in-from-top',
                    side === 'bottom' &&
                        'tw-inset-x-0 tw-bottom-0 tw-h-auto tw-border-t data-[state=closed]:tw-slide-out-to-bottom data-[state=open]:tw-slide-in-from-bottom',
                    className
                )}
                {...props}
            >
                {children}
                <SheetPrimitive.Close className="tw-rounded-xs focus:tw-outline-hidden disabled:tw-pointer-events-none tw-absolute tw-right-4 tw-top-4 tw-opacity-70 tw-ring-offset-background tw-transition-opacity hover:tw-opacity-100 focus:tw-ring-2 focus:tw-ring-ring focus:tw-ring-offset-2 data-[state=open]:tw-bg-secondary">
                    <XIcon className="tw-size-4" />
                    <span className="tw-sr-only">Close</span>
                </SheetPrimitive.Close>
            </SheetPrimitive.Content>
        </SheetPortal>
    );
}

function SheetHeader({ className, ...props }) {
    return (
        <div
            data-slot="sheet-header"
            className={cn('tw-flex tw-flex-col tw-gap-1.5 tw-p-4', className)}
            {...props}
        />
    );
}

function SheetFooter({ className, ...props }) {
    return (
        <div
            data-slot="sheet-footer"
            className={cn('tw-mt-auto tw-flex tw-flex-col tw-gap-2 tw-p-4', className)}
            {...props}
        />
    );
}

function SheetTitle({ className, ...props }) {
    return (
        <SheetPrimitive.Title
            data-slot="sheet-title"
            className={cn('tw-font-semibold tw-text-foreground', className)}
            {...props}
        />
    );
}

function SheetDescription({ className, ...props }) {
    return (
        <SheetPrimitive.Description
            data-slot="sheet-description"
            className={cn('tw-text-sm tw-text-muted-foreground', className)}
            {...props}
        />
    );
}

export {
    Sheet,
    SheetTrigger,
    SheetClose,
    SheetContent,
    SheetHeader,
    SheetFooter,
    SheetTitle,
    SheetDescription,
};
